import { useState, useEffect } from 'react';
import { Icon } from '../../icons';
import { Badge, Card, SectionHeader, DataTable, Field } from '../../components/ui';
import { useSettings } from '../../context/SettingsContext';
import { useSubscription } from '../../hooks/useSubscription';
import FeatureGate from '../../components/subscription/FeatureGate';
import api from '../../api/axios';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

export default function FireAlertScreen() {
  const { language = 'en' } = useSettings();
  const { hasFeature } = useSubscription();
  const hasMultichannel = hasFeature('has_multichannel');

  const t = (key, params) => {
    let str = translations[language]?.alert?.[key] || translations.en?.alert?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };

  const [phase, setPhase] = useState("idle");
  const [log, setLog] = useState([]);
  const [alertType, setAlertType] = useState("fire");
  const [channels, setChannels] = useState({ sms: false, email: true, push: true, whatsapp: false });
  const [scope, setScope] = useState("all");
  const [customMsg, setCustomMsg] = useState("");
  const [stats, setStats] = useState({ all: 0, owners: 0, staff: 0 });
  const [broadcastStats, setBroadcastStats] = useState(null);

  useEffect(() => {
    // Force non-standard channels off if tier doesn't support them
    if (!hasMultichannel) {
      setChannels(c => ({ ...c, sms: false, whatsapp: false }));
    }
  }, [hasMultichannel]);

  useEffect(() => {
    api.get('/api/v1/alerts/emergency/stats').then(res => {
      if (res?.success) {
        setStats(res.data);
      }
    }).catch(err => console.error(err));
  }, []);

  const ALERT_TYPES = [
    { id: "fire", label: t("alert_fire"), color: "#ef4444", bg: "#fef2f2", border: "#fca5a5", urgency: t("urgency_critical") },
    { id: "flood", label: t("alert_flood"), color: "#3b82f6", bg: "#eff6ff", border: "#93c5fd", urgency: t("urgency_high") },
    { id: "gas", label: t("alert_gas"), color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", urgency: t("urgency_critical") },
    { id: "security", label: t("alert_security"), color: "#7c3aed", bg: "#f5f3ff", border: "#c4b5fd", urgency: t("urgency_high") },
    { id: "power", label: t("alert_power"), color: "#64748b", bg: "#f8fafc", border: "#e2e8f0", urgency: t("urgency_medium") },
    { id: "medical", label: t("alert_medical"), color: "#10b981", bg: "#f0fdf4", border: "#86efac", urgency: t("urgency_critical") },
  ];
  const atype = ALERT_TYPES.find(a => a.id === alertType) || ALERT_TYPES[0];

  const targetUsersCount = stats[scope] || 0;
  const chCount = Object.values(channels).filter(Boolean).length;
  const totalNotif = targetUsersCount * chCount;

  const sendAlert = async () => {
    setPhase("sending");
    setLog([{ msg: t("log_initializing"), color: "#4b5563", time: new Date().toLocaleTimeString() }]);

    try {
      const payload = {
        type: alertType,
        customMsg: customMsg,
        channels: channels,
        scope: scope,
        atype: atype
      };

      const res = await api.post('/api/v1/alerts/emergency', payload);
      if (res?.success) {
        setBroadcastStats(res.stats);
        setLog(l => [...l, { msg: "Alert broadcast completed successfully.", color: "#4ade80", time: new Date().toLocaleTimeString() }]);
        setTimeout(() => setPhase("sent"), 1000);
      }
    } catch (err) {
      console.error(err);
      setLog(l => [...l, { msg: err.response?.data?.message || "Failed to broadcast alert.", color: "#ef4444", time: new Date().toLocaleTimeString() }]);
      setTimeout(() => setPhase("idle"), 3000);
    }
  };

  const reset = () => { setPhase("idle"); setLog([]); setCustomMsg(""); setBroadcastStats(null); };

  return (
    <FeatureGate feature="has_emergency_alert" message="Emergency Alert System is available on Gold+ plans.">
      <div className="fade-up space-y-4 max-w-4xl">

        {phase === "sent" && (
          <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg,#0f172a 0%,#1e1b4b 100%)" }}>
            <div className="p-10 text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: "rgba(52,211,153,0.15)" }}>
                <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Icon name="check" size={28} color="white" strokeWidth={2.5} />
                </div>
              </div>
              <div className="text-white text-2xl font-black mb-2">{t("title_broadcast_complete")}</div>
              <div className="text-white/50 text-sm mb-8">{t("desc_broadcast_complete", { totalNotif: broadcastStats?.total_users || 0, users: broadcastStats?.total_users || 0 })}</div>
              <div className="grid grid-cols-4 gap-3 max-w-lg mx-auto mb-8">
                {[[t("label_sms_stats"), broadcastStats?.sms || 0], [t("label_email_stats"), broadcastStats?.email || 0], [t("label_push_stats"), broadcastStats?.push || 0], [t("label_wa_stats") || "WhatsApp", broadcastStats?.whatsapp || 0]].map(([l, v]) => (
                  <div key={l} className="rounded-xl p-3 text-center" style={{ background: "rgba(255,255,255,0.08)" }}>
                    <div className="text-white font-black text-xl">{v}</div>
                    <div className="text-white/40 text-xs mt-1">{l}</div>
                  </div>
                ))}
              </div>
              <button onClick={reset} className="px-8 py-3 rounded-xl text-sm font-semibold cursor-pointer border-0" style={{ background: "rgba(255,255,255,0.12)", color: "white", fontFamily: "inherit" }}>{t("btn_send_another")}</button>
            </div>
          </div>
        )}

        {phase === "sending" && (
          <Card>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: atype.bg, border: `2px solid ${atype.border}` }}>
                <Icon name="siren" size={18} color={atype.color} />
              </div>
              <div className="flex-1">
                <div className="font-black text-ink">{t("title_broadcasting")}</div>
                <div className="text-xs text-muted">{t("subtitle_broadcasting")}</div>
              </div>
            </div>
            <div className="rounded-xl p-4 font-mono text-xs space-y-1.5 max-h-56 overflow-y-auto" style={{ background: "#0d1117" }}>
              {log.map((e, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span style={{ color: "#4b5563", flexShrink: 0 }}>{e.time}</span>
                  <span style={{ color: e.color }}>{e.msg}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {(phase === "idle" || phase === "confirm") && (
          <>
            <Card>
              <SectionHeader title={t("title_alert_system")} subtitle={t("subtitle_alert_system")} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {ALERT_TYPES.map(t => (
                  <div key={t.id} onClick={() => setAlertType(t.id)}
                    className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${alertType === t.id ? '' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50'}`}
                    style={{ borderColor: alertType === t.id ? t.color : undefined, background: alertType === t.id ? t.color + '1A' : undefined }}>
                    <div>
                      <div className={`text-xs font-bold ${alertType !== t.id ? 'text-slate-500 dark:text-slate-400' : ''}`} style={{ color: alertType === t.id ? t.color : undefined }}>{t.label}</div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: alertType === t.id ? t.color : undefined, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.06em" }} className={alertType !== t.id ? 'text-slate-400 dark:text-slate-500' : ''}>
                        {t.urgency}
                      </div>
                    </div>
                    {alertType === t.id && (
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: t.color }}>
                        <Icon name="check" size={10} color="white" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <SectionHeader title={t("title_channels")} subtitle={t("subtitle_channels")} />
                {[{ k: "sms", label: t("channel_sms"), desc: t("desc_sms"), icon: "phone_call", restricted: !hasMultichannel }, { k: "email", label: t("channel_email"), desc: t("desc_email"), icon: "mail" }, { k: "push", label: t("channel_push"), desc: t("desc_push"), icon: "bell_ring" }, { k: "whatsapp", label: t("channel_whatsapp"), desc: t("desc_whatsapp"), icon: "wifi", restricted: !hasMultichannel }].map(ch => (
                  <div key={ch.k} className={`flex items-center justify-between py-2.5 border-b border-slate-50 dark:border-slate-700/50 last:border-0 ${ch.restricted ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-2.5">
                      <Icon name={ch.icon} size={13} className="text-muted flex-shrink-0" />
                      <div>
                        <div className="text-sm font-semibold text-ink flex items-center gap-2">
                          {ch.label}
                          {ch.restricted && <Badge color="amber" sm>GoldMax Only</Badge>}
                        </div>
                        <div className="text-sm text-muted">{ch.desc}</div>
                      </div>
                    </div>
                    <button 
                      onClick={() => !ch.restricted && setChannels(c => ({ ...c, [ch.k]: !c[ch.k] }))} 
                      className={`flex-shrink-0 border-0 p-0 ${ch.restricted ? 'cursor-not-allowed' : 'cursor-pointer'} ${!channels[ch.k] ? 'bg-slate-200 dark:bg-slate-700' : ''}`}
                      style={{ width: 38, height: 21, borderRadius: 11, background: channels[ch.k] ? "#4f46e5" : undefined, transition: "background .2s", position: "relative" }}>
                      <div style={{ width: 15, height: 15, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: channels[ch.k] ? 20 : 3, transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                    </button>
                  </div>
                ))}
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted">{t("label_estimated_reach")}</span>
                    <span className="font-black text-ink">{t("text_notifications_count", { count: totalNotif })}</span>
                  </div>
                </div>
              </Card>

              <Card>
                <SectionHeader title={t("title_scope")} />
                <div className="space-y-2 mb-4">
                  {[{ id: "all", label: t("scope_all"), count: stats.all }, { id: "owners", label: t("scope_owners"), count: stats.owners }, { id: "staff", label: t("scope_staff"), count: stats.staff }].map(s => (
                    <div key={s.id} onClick={() => setScope(s.id)}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${scope === s.id ? 'border-primary bg-indigo-50 dark:bg-indigo-500/10' : 'border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800/50'}`}>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${scope === s.id ? 'border-primary bg-primary' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'}`}>
                        {scope === s.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-ink">{s.label}</div>
                        <div className="text-sm text-muted">{s.count} Users Selected</div>
                      </div>
                    </div>
                  ))}
                </div>
                <Field label={t("label_custom_msg")} type="textarea" rows={2} value={customMsg} onChange={e => setCustomMsg(e.target.value)} placeholder={t("placeholder_custom_msg")} />
              </Card>
            </div>

            <div className="rounded-2xl overflow-hidden border-2" style={{ borderColor: atype.color }}>
              <div className="flex items-center gap-4 p-5" style={{ background: atype.color + '1A' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: atype.color }}>
                  <Icon name="fire" size={22} color="white" />
                </div>
                <div className="flex-1">
                  <div className="font-black text-ink">{atype.label}</div>
                  <div className="text-xs text-muted mt-0.5">
                    Will notify <strong>{targetUsersCount} {t("text_users")}</strong> via <strong>{chCount} {t("text_channels", { s: chCount !== 1 ? t("text_plural_s") : "" })}</strong> - <strong>{totalNotif} {t("text_total_notifications")}</strong>
                  </div>
                </div>
                {phase === "idle" ? (
                  <button onClick={() => setPhase("confirm")}
                    className="px-6 py-3 rounded-xl font-bold text-sm text-white cursor-pointer border-0 transition-all hover:opacity-90 active:scale-95"
                    style={{ background: atype.color, boxShadow: `0 4px 16px ${atype.color}55`, fontFamily: "inherit" }}>{t("btn_trigger_alert")}</button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={sendAlert}
                      className="px-6 py-3 rounded-xl font-bold text-sm text-white cursor-pointer border-0"
                      style={{ background: atype.color, fontFamily: "inherit" }}>{t("btn_confirm_send")}</button>
                    <button onClick={() => setPhase("idle")}
                      className="px-4 py-3 rounded-xl font-semibold text-sm cursor-pointer bg-white text-slate-700 border border-border hover:bg-slate-50 transition-all"
                      style={{ fontFamily: "inherit" }}>{t("btn_cancel")}</button>
                  </div>
                )}
              </div>
              {phase === "confirm" && (
                <div className="px-5 py-3 flex items-center gap-2 text-xs font-semibold" style={{ background: "#fef3c7", borderTop: "1px solid #fde68a", color: "#92400e" }}>
                  <Icon name="alert" size={13} color="#d97706" />
                  {t("msg_confirm_warning", { count: targetUsersCount })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </FeatureGate>
  );
}
